
import { createContext, useContext, ReactNode, useState, useEffect, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { UserType } from '@/data/campaign-types';
import { useAuthState } from '@/hooks/use-auth-state';
import { useAuthMethods } from '@/hooks/use-auth-methods';
import { supabase } from '@/integrations/supabase/client';
import { logError } from "@/lib/sentry";
import {
  clearCachedValue,
  readCachedValue,
  writeCachedValue,
} from "@/lib/beta-access-cache";
import { fetchCreatorPaymentProfile } from "@/lib/payment-profile-rpcs";

export interface UserDetails {
  id: string;
  email: string;
  user_type: UserType;
  full_name?: string;
  company?: string;
  avatar_url?: string;
  bio?: string;
  website?: string;
  created_at: string;
  // Marketer-specific fields (from marketers table via left join)
  paypal_name?: string;
  paypal_user_id?: string;
  paypal_verified_at?: string;
  paypal_charges_enabled?: boolean;
  card_verified_at?: string;
  stripe_customer_id?: string;
  stripe_charges_enabled?: boolean;
  primary_payment_method?: 'stripe' | 'paypal';
  email_notifications?: {
    campaign_applications?: boolean;
  };
  status?: 'pending' | 'approved' | 'rejected';
  // Creator-specific fields (for convenience when user_type is creator)
  paypal_payer_id?: string;
  // Ban fields
  is_banned?: boolean;
  banned_at?: string;
  ban_reason?: string;
}

export interface CreatorDetails {
  country?: string | null;
  tiktok_username?: string;
  tiktok_accounts_connected?: boolean;
  instagram_accounts_connected?: boolean;
  youtube_accounts_connected?: boolean;
  youtube_handle?: string | null;
  stripe_onboarding_complete?: boolean;
  paypal_payer_id?: string;
  paypal_connected?: boolean;
  paypal_email?: string;
  payout_readiness_status?:
    | "not_connected"
    | "pending_probe"
    | "pending_probe_retry"
    | "ready"
    | "blocked_country"
    | "blocked_other";
  payout_readiness_code?: string | null;
  payout_readiness_message?: string | null;
  payout_readiness_updated_at?: string | null;
  email_notifications?: {
    campaign_started?: boolean;
  };
}

export interface BetaPlatformAccess {
  enabled: boolean;
  oauthEnabled: boolean;
  ui: boolean;
  oauth: boolean;
  hiddenCampaigns: boolean;
  adminTools: boolean;
}

export interface BetaAccess {
  youtube: BetaPlatformAccess;
  creatorRoster: {
    enabled: boolean;
    ui: boolean;
  };
}

const EMPTY_BETA_ACCESS: BetaAccess = {
  youtube: {
    enabled: false,
    oauthEnabled: false,
    ui: false,
    oauth: false,
    hiddenCampaigns: false,
    adminTools: false,
  },
  creatorRoster: {
    enabled: false,
    ui: false,
  },
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userDetails: UserDetails | null;
  creatorDetails: CreatorDetails | null;
  creatorDetailsLoading: boolean;
  loading: boolean;
  adminLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    userType: UserType,
    userData: any
  ) => Promise<{ requiresEmailConfirmation: boolean }>;
  signOut: () => Promise<void>;
  updateUserDetails: (details: Partial<UserDetails>) => Promise<void>;
  resetPassword: (email: string) => Promise<{ success: boolean }>;
  refreshCreatorDetails: () => Promise<void>;
  updateCreatorDetails: (details: Partial<CreatorDetails>) => void;
  userType: UserType | null;
  setUserType: (type: UserType) => void;
  clearUserType: () => void;
  isInitialized: boolean;
  isAdmin: boolean;
  betaAccess: BetaAccess;
  betaAccessLoading: boolean;
  refreshBetaAccess: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user, session, userDetails, loading, setUserDetails } =
    useAuthState();
  const { signIn, signUp, signOut, updateUserDetails, resetPassword } =
    useAuthMethods(user, setUserDetails);

  // UserType state (from UserProvider)
  const [isInitialized, setIsInitialized] = useState(false);
  const [userType, setUserTypeState] = useState<UserType | null>(null);
  const [creatorDetails, setCreatorDetails] = useState<CreatorDetails | null>(
    null
  );
  const [creatorDetailsLoading, setCreatorDetailsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);
  const [adminCheckedUserId, setAdminCheckedUserId] = useState<string | null>(null);
  const [betaAccess, setBetaAccess] = useState<BetaAccess>(EMPTY_BETA_ACCESS);
  const [betaAccessLoading, setBetaAccessLoading] = useState(false);
  const [betaAccessCheckedUserId, setBetaAccessCheckedUserId] = useState<string | null>(null);
  const lastCreatorDetailsFetchAtMs = useRef<number>(0);
  const lastBetaAccessUserIdRef = useRef<string | null>(null);
  const userId = user?.id ?? null;
  const currentAuthUserIdRef = useRef<string | null>(userId);
  currentAuthUserIdRef.current = userId;
  // Initialize from localStorage on first render
  useEffect(() => {
    try {
      const storedValue = localStorage.getItem("userType");
      const parsedValue = storedValue ? JSON.parse(storedValue) : null;
      setUserTypeState(parsedValue);
      setIsInitialized(true);
    } catch (error) {
      logError("Error reading from localStorage:", error);
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const checkAdminStatus = async () => {
      if (loading) {
        if (mounted) {
          setAdminLoading(true);
          setAdminCheckedUserId(null);
        }
        return;
      }

      if (!userId) {
        if (mounted) {
          setIsAdmin(false);
          setAdminCheckedUserId(null);
          setAdminLoading(false);
        }
        return;
      }

      setAdminLoading(true);
      setAdminCheckedUserId(null);

      const { data, error } = await (supabase.rpc as any)("is_current_admin");

      if (!mounted) return;

      if (error) {
        logError("Error checking admin status:", error);
      }

      setIsAdmin(!error && data === true);
      setAdminCheckedUserId(userId);
      setAdminLoading(false);
    };

    checkAdminStatus();

    return () => {
      mounted = false;
    };
  }, [loading, userId]);

  const getBetaAccessStorage = () =>
    typeof window !== "undefined" ? window.sessionStorage : null;

  const fetchBetaAccess = async ({ force = false }: { force?: boolean } = {}) => {
    const requestedUserId = user?.id ?? null;

    if (!requestedUserId || !session?.access_token) {
      setBetaAccess(EMPTY_BETA_ACCESS);
      setBetaAccessCheckedUserId(null);
      setBetaAccessLoading(false);
      return;
    }

    if (!force) {
      const cached = readCachedValue<BetaAccess>(getBetaAccessStorage(), requestedUserId);
      if (cached) {
        if (currentAuthUserIdRef.current !== requestedUserId) return;
        setBetaAccess({
          youtube: {
            ...EMPTY_BETA_ACCESS.youtube,
            ...(cached.youtube ?? {}),
          },
          creatorRoster: {
            ...EMPTY_BETA_ACCESS.creatorRoster,
            ...(cached.creatorRoster ?? {}),
          },
        });
        setBetaAccessCheckedUserId(requestedUserId);
        setBetaAccessLoading(false);
        return;
      }
    } else {
      clearCachedValue(getBetaAccessStorage(), requestedUserId);
    }

    setBetaAccessLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("beta-access");
      if (error) {
        throw error;
      }

      const resolvedBetaAccess = {
        youtube: {
          ...EMPTY_BETA_ACCESS.youtube,
          ...(data?.youtube ?? {}),
        },
        creatorRoster: {
          ...EMPTY_BETA_ACCESS.creatorRoster,
          ...(data?.creatorRoster ?? {}),
        },
      };

      if (currentAuthUserIdRef.current !== requestedUserId) return;
      writeCachedValue(getBetaAccessStorage(), requestedUserId, resolvedBetaAccess);
      setBetaAccess(resolvedBetaAccess);
    } catch (error) {
      if (currentAuthUserIdRef.current !== requestedUserId) return;
      logError("Error fetching beta access:", error);
      clearCachedValue(getBetaAccessStorage(), requestedUserId);
      setBetaAccess(EMPTY_BETA_ACCESS);
    } finally {
      if (currentAuthUserIdRef.current === requestedUserId) {
        setBetaAccessCheckedUserId(requestedUserId);
        setBetaAccessLoading(false);
      }
    }
  };

  useEffect(() => {
    const previousUserId = lastBetaAccessUserIdRef.current;
    if (previousUserId && previousUserId !== user?.id) {
      clearCachedValue(getBetaAccessStorage(), previousUserId);
    }

    lastBetaAccessUserIdRef.current = user?.id ?? null;
    void fetchBetaAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Set user type when userDetails are available
  useEffect(() => {
    if (userDetails?.user_type && userType !== userDetails.user_type) {
      setUserType(userDetails.user_type);
    }
  }, [userDetails, userType]);

  const fetchCreatorDetails = async ({ force }: { force: boolean }) => {
    if (!user || !userDetails || userDetails.user_type !== "creator") {
      setCreatorDetails(null);
      setCreatorDetailsLoading(false);
      lastCreatorDetailsFetchAtMs.current = 0;
      return;
    }

    // Avoid hammering Supabase on focus/visibility changes.
    const ttlMs = 30_000;
    const now = Date.now();
    if (
      !force &&
      lastCreatorDetailsFetchAtMs.current > 0 &&
      now - lastCreatorDetailsFetchAtMs.current < ttlMs
    ) {
      return;
    }

    const requestedUserId = user.id;
    setCreatorDetailsLoading(true);
    try {
      const [creatorQuery, countryQuery, paymentProfileQuery, tiktokQuery, instagramQuery, youtubeQuery] = await Promise.all([
        supabase
          .from("creators")
          // Payment identifiers remain behind the authorized payment-profile RPC.
          .select("tiktok_username, youtube_handle, stripe_onboarding_complete, paypal_connected, payout_readiness_status, payout_readiness_code, payout_readiness_message, payout_readiness_updated_at, email_notifications")
          .eq("id", requestedUserId)
          .single(),
        (supabase as any).rpc("get_my_creator_country"),
        fetchCreatorPaymentProfile(requestedUserId),
        supabase.from("creator_tiktok_accounts").select("id")
          .eq("creator_id", requestedUserId).eq("is_archived", false).limit(1),
        supabase.from("creator_instagram_accounts").select("id")
          .eq("creator_id", requestedUserId).eq("is_archived", false).limit(1),
        supabase.from("creator_youtube_accounts").select("id")
          .eq("creator_id", requestedUserId).eq("is_archived", false)
          .eq("oauth_status", "verified").not("verified_at", "is", null).limit(1),
      ]);
      if (currentAuthUserIdRef.current !== requestedUserId) return;
      const { data: creatorBaseData, error } = creatorQuery;
      if (error) throw error;
      const { data: creatorCountry, error: creatorCountryError } = countryQuery;
      if (creatorCountryError) throw creatorCountryError;
      const { data: paymentProfile, error: paymentProfileError } = paymentProfileQuery;

      if (paymentProfileError) {
        logError("Error fetching creator payment profile:", paymentProfileError);
      }

      const data = {
        ...(creatorBaseData ?? {}),
        country: creatorCountry ?? null,
        ...(paymentProfileError ? {} : paymentProfile ?? {}),
      };

      const inferredReady = !!data?.paypal_payer_id;
      const normalizedReadinessStatus =
        (data as any)?.payout_readiness_status || (inferredReady ? "ready" : "not_connected");
      const normalizedReadinessMessage =
        (data as any)?.payout_readiness_message ||
        (normalizedReadinessStatus === "ready"
          ? "PayPal receive capability verified."
          : "Connect PayPal and check Account Settings before receiving payouts.");
      const normalizedReadinessCode = (data as any)?.payout_readiness_code ?? null;
      const normalizedReadinessUpdatedAt = (data as any)?.payout_readiness_updated_at ?? null;

      const tiktokAccountsConnected = !tiktokQuery.error && Boolean(tiktokQuery.data?.length);
      const instagramAccountsConnected = !instagramQuery.error && Boolean(instagramQuery.data?.length);
      const youtubeAccountsConnected = !youtubeQuery.error && Boolean(youtubeQuery.data?.length);

      setCreatorDetails({
        ...(data ?? {}),
        payout_readiness_status: normalizedReadinessStatus,
        payout_readiness_code: normalizedReadinessCode,
        payout_readiness_message: normalizedReadinessMessage,
        payout_readiness_updated_at: normalizedReadinessUpdatedAt,
        tiktok_accounts_connected: tiktokAccountsConnected,
        instagram_accounts_connected: instagramAccountsConnected,
        youtube_accounts_connected: youtubeAccountsConnected,
      });
      lastCreatorDetailsFetchAtMs.current = now;
    } catch (error) {
      if (currentAuthUserIdRef.current !== requestedUserId) return;
      logError("Error fetching creator details:", error);
      // Allow retries after transient errors.
      lastCreatorDetailsFetchAtMs.current = 0;
    } finally {
      if (currentAuthUserIdRef.current === requestedUserId) {
        setCreatorDetailsLoading(false);
      }
    }
  };

  // Fetch creator details when user identity/type changes.
  useEffect(() => {
    fetchCreatorDetails({ force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, userDetails?.user_type]);

  // Refresh creator details when returning to the app/tab (guarded by TTL).
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchCreatorDetails({ force: false });
      }
    };
    const handleFocus = () => {
      fetchCreatorDetails({ force: false });
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, userDetails?.user_type]);

  const setUserType = (type: UserType) => {
    try {
      setUserTypeState(type);
      localStorage.setItem("userType", JSON.stringify(type));
    } catch (error) {
      logError("Error writing to localStorage:", error);
    }
  };

  const clearUserType = () => {
    try {
      setUserTypeState(null);
      localStorage.removeItem("userType");
    } catch (error) {
      logError("Error clearing localStorage:", error);
    }
  };

  const refreshCreatorDetails = async () => {
    await fetchCreatorDetails({ force: true });
  };

  const updateCreatorDetails = (details: Partial<CreatorDetails>) => {
    setCreatorDetails((prev) => ({ ...(prev ?? {}), ...details }));
  };

  const effectiveAdminLoading =
    loading || adminLoading || (Boolean(user) && adminCheckedUserId !== user.id);
  const effectiveIsAdmin = Boolean(user && adminCheckedUserId === user.id && isAdmin);
  const effectiveBetaAccessLoading =
    loading || betaAccessLoading || Boolean(user && betaAccessCheckedUserId !== user.id);

  const value = {
    user,
    session,
    userDetails,
    creatorDetails,
    creatorDetailsLoading,
    loading,
    adminLoading: effectiveAdminLoading,
    signIn,
    signUp,
    signOut,
    updateUserDetails,
    resetPassword,
    refreshCreatorDetails,
    updateCreatorDetails,
    userType,
    setUserType,
    clearUserType,
    isInitialized,
    isAdmin: effectiveIsAdmin,
    betaAccess,
    betaAccessLoading: effectiveBetaAccessLoading,
    refreshBetaAccess: () => fetchBetaAccess({ force: true }),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useUser = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useUser must be used within an AuthProvider");
  }
  return {
    userType: context.userType,
    setUserType: context.setUserType,
    clearUserType: context.clearUserType,
    isInitialized: context.isInitialized,
  };
};
